/* eslint-disable @typescript-eslint/no-non-null-assertion */
import Test from '../models/Test';
import TestPart from '../models/TestPart';
import Section from '../models/Section';
import Item from '../models/Item';
import CompanionMaterialBuilder from './CompanionMaterialBuilder';
import RubricBlock from '@/models/RubricBlock';
import ChoiceInteraction from '@/models/ChoiceInteraction';
import DeliveryOption from '@/controllers/options/DeliveryOption';

class TestModelBuilder {
  private static readonly SECTION_ELEMENT = 'qti-assessment-section';
  private static readonly IDENTIFIER_ATTRIBUTE = 'identifier';
  private static readonly TITLE_ATTRIBUTE = 'title';

  private readonly _serviceUrl: string | undefined;

  constructor(serviceUrl: string | undefined) {
    this._serviceUrl = serviceUrl;
  }

  public build(testXml: string, qtiItems: Array<Map<string, string>>): Test {
    const document: Document = new DOMParser().parseFromString(testXml, 'text/html');
    const testElement: Element = document.getElementsByTagName('qti-assessment-test')[0];
    const test: Test = this.createTest(testElement);

    Array.from(testElement.children)
      .filter((element) => element.nodeName.toLowerCase() === 'qti-test-part')
      .forEach((testPartElement) => {
        const testPart: TestPart = this.createTestPart(testPartElement);
        test.addTestPart(testPart);

        Array.from(testPartElement.children)
          .filter((element) => element.nodeName.toLowerCase() === TestModelBuilder.SECTION_ELEMENT)
          .forEach((sectionElement) => {
            const section: Section = this.createSection(sectionElement);
            testPart.addSection(section);

            this.addSectionsAndItems(sectionElement, section, qtiItems);
          });
      });

    return test;
  }

  private addSectionsAndItems(sectionElement: Element, section: Section, qtiItems: Array<Map<string, string>>): void {
    if (sectionElement) {
      this.addRubricBlocks(
        section,
        Array.from(sectionElement.children).filter((element) => element.nodeName.toLowerCase() === 'qti-rubric-block')
      );

      this.addDeliveryOptions(
        section,
        Array.from(sectionElement.children).filter((element) => element.nodeName.toLowerCase() === 'access-for-all-pnp')
      );

      Array.from(sectionElement.children)
        .filter((element) => element.nodeName.toLowerCase() === 'qti-assessment-item-ref')
        .forEach((itemElement) => {
          section.addItem(this.createItem(itemElement, qtiItems));
        });

      Array.from(sectionElement.children)
        .filter((element) => element.nodeName.toLowerCase() === TestModelBuilder.SECTION_ELEMENT)
        .forEach((subSectionElement) => {
          const subSection: Section = this.createSection(subSectionElement);
          section.addSubSection(subSection);
          this.addSectionsAndItems(subSectionElement, subSection, qtiItems);
        });
    }
  }

  private createTest(testElement: Element): Test {
    return new Test(
      testElement.getAttribute(TestModelBuilder.IDENTIFIER_ATTRIBUTE)!,
      testElement.getAttribute(TestModelBuilder.TITLE_ATTRIBUTE)!
    );
  }

  private createTestPart(testPartElement: Element): TestPart {
    return new TestPart(
      testPartElement.getAttribute(TestModelBuilder.IDENTIFIER_ATTRIBUTE)!,
      testPartElement.getAttribute('submission-mode')!,
      testPartElement.getAttribute('navigation-mode')!
    );
  }

  private createSection(sectionElement: Element): Section {
    return new Section(
      sectionElement.getAttribute(TestModelBuilder.IDENTIFIER_ATTRIBUTE)!,
      sectionElement.getAttribute(TestModelBuilder.TITLE_ATTRIBUTE)!,
      this.stringToBoolean(sectionElement.getAttribute('visible')!),
      this.stringToBoolean(sectionElement.getAttribute('required')!)
    );
  }

  private createItem(itemElement: Element, qtiItems: Array<Map<string, string>>): Item {
    const itemIdentifier: string = itemElement.getAttribute(TestModelBuilder.IDENTIFIER_ATTRIBUTE)!;
    if (!itemIdentifier) {
      throw new Error('Identifier not found for item element');
    }
    const item: Item = new Item(
      itemIdentifier,
      itemElement.getAttribute('name')!,
      this.getItemQti(itemIdentifier, qtiItems)
    );
    CompanionMaterialBuilder.build(item);
    this.addChoiceInteractions(item);
    return item;
  }

  private addChoiceInteractions(item: Item): void {
    const document: Document = new DOMParser().parseFromString(item.qti, 'text/html');

    document.querySelectorAll('qti-choice-interaction').forEach((element) => {
      const identifier: string | null = this.getAttribute(element, 'response-identifier', '');
      const minChoices: string | null = this.getAttribute(element, 'min-choices', '0');
      const maxChoices: string | null = this.getAttribute(element, 'max-choices', '0');

      item.addChoiceInteraction(
        new ChoiceInteraction(
          identifier ? identifier : '',
          minChoices ? parseInt(minChoices) : 0,
          maxChoices ? +parseInt(maxChoices) : 0
        )
      );
    });
  }

  private addRubricBlocks(section: Section, rubricBlockArray: Array<Element>): void {
    if (rubricBlockArray && rubricBlockArray.length > 0) {
      for (let i = 0; i < rubricBlockArray.length; i++) {
        const rubricBlockElement: Element = rubricBlockArray[i];
        section.addRubricBlock(
          new RubricBlock(
            rubricBlockElement.outerHTML,
            this.getAttribute(rubricBlockElement, 'stylesheet', '')!,
            this.getAttribute(rubricBlockElement, 'view', '')!,
            this.getAttribute(rubricBlockElement, 'use', '')!
          )
        );
      }
    }
  }

  private addDeliveryOptions(section: Section, pnpArray: Array<Element>): void {
    if (pnpArray && pnpArray.length > 0) {
      for (let i = 0; i < pnpArray[0].children.length; i++) {
        const pnpItemElement: Element = pnpArray[0].children[i];
        const deliveryOption: DeliveryOption = new DeliveryOption(pnpItemElement.tagName.toLowerCase());
        pnpItemElement.getAttributeNames().forEach((attributeName) => {
          deliveryOption.addSetting(attributeName, this.getAttribute(pnpItemElement, attributeName, '')!);
        });
        if (deliveryOption.name === 'calculator-on-screen') {
          deliveryOption.name = deliveryOption.settings.get('calculator-type')?.toLowerCase() + '-calculator';
        }

        section.addDeliveryOption(deliveryOption);
      }
    }
  }

  private getItemQti(identifier: string, qtiItems: Array<Map<string, string>>): string {
    let qti: string = qtiItems.find((element) => element.get('identifier') === identifier)!.get('qti')!;

    if (!qti) {
      throw new Error('qti not found for item ' + identifier);
    }
    qti = this.updateStimulusRef(qti);
    qti = this.updateQtiIdentifier(qti, identifier);
    return qti;
  }

  private updateStimulusRef(qti: string): string {
    if (qti && this._serviceUrl) {
      const document: Document = new DOMParser().parseFromString(qti, 'text/html');
      document.querySelectorAll('qti-assessment-stimulus-ref').forEach((element) => {
        const hrefValue = this.getAttribute(element, 'href', null);
        if (hrefValue) {
          element.setAttribute('href', this._serviceUrl + hrefValue);
        }
      });
      return new XMLSerializer().serializeToString(document);
    } else {
      return qti;
    }
  }

  private updateQtiIdentifier(qti: string, expectedIdentifier: string) {
    const document: Document = new DOMParser().parseFromString(qti, 'text/html');
    const elements: NodeListOf<Element> = document.querySelectorAll('qti-assessment-item');
    if (elements.length != 1) {
      throw new Error('Unexpected qti structure for item: ' + expectedIdentifier);
    }

    elements[0]!.setAttribute('identifier', expectedIdentifier);
    return new XMLSerializer().serializeToString(document);
  }

  private stringToBoolean(value: string): boolean {
    return value ? value === 'true' : false;
  }

  private getAttribute(element: Element, key: string, defaultValue: string | null): string | null {
    let value: string | null = defaultValue;
    if (element) {
      const attributeValue: string | null = element.getAttribute(key);
      if (attributeValue) {
        value = attributeValue;
      }
    }
    return value;
  }
}

export default TestModelBuilder;
